import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SatCoursePath } from '@/components/sat/journey/SatCoursePath';
import { Button } from '@/components/ui/button';
import { DIAGNOSTIC_FACTS, MARKETING_CTAS } from '@/lib/marketingCtas';
import { DEMO_ACTIVE_DOMAIN, demoDomainMap } from '@/pages/open/openDemoDtos';

/**
 * Starting Line for /what-we-do/sat/about (spec 010 §5.4): H1, one primary, and the real course
 * path (`SatCoursePath`, trail variant) filled with the example student.
 *
 * The trail's own unfold and "living current" animations are switched off here: spec 010 §6 says
 * nothing animates on page load above the fold. The override is scoped to this wrapper; the
 * component itself is untouched. The artifact is `inert` (display only): its stops are buttons in
 * the real app, but here they would be dead controls.
 */
const STATIC_TRAIL_CSS = `
.sat-about-static .sat-ribbon-solid { animation: none !important; stroke-dashoffset: 0 !important; }
.sat-about-static .sat-ribbon-ahead { animation: none !important; opacity: 1 !important; }
.sat-about-static .sat-node { animation: none !important; opacity: 1 !important; }
.sat-about-static .sat-ribbon-energy, .sat-about-static .animate-ping { display: none !important; }
`;

export function SatAboutHero() {
  const domains = useMemo(() => demoDomainMap(), []);
  const cta = MARKETING_CTAS.courseAboutPrimary;
  const secondary = MARKETING_CTAS.courseAboutSecondary;

  return (
    <section aria-labelledby="about-title" className="mx-auto w-full max-w-6xl px-4 pb-14 pt-10 sm:px-6 lg:pb-20 lg:pt-14">
      <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] lg:gap-20">
        <div>
          <h1 id="about-title" className="max-w-[14ch] text-balance text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Everything in the free SAT course, explained.
          </h1>
          <p className="mt-5 max-w-[38ch] text-lg leading-relaxed text-muted-foreground sm:text-xl">
            Take the diagnostic today, no account. Here&apos;s what happens after.
          </p>
          <div className="mt-8 flex flex-col items-stretch gap-4 sm:flex-row sm:items-center sm:gap-6">
            <Button asChild size="lg" className="h-12 px-7 text-base">
              <Link to={cta.href}>{cta.label}</Link>
            </Button>
            <Link
              to={secondary.href}
              className="self-center rounded text-sm font-medium underline underline-offset-4 outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-primary"
            >
              {secondary.label}
            </Link>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            The diagnostic is {DIAGNOSTIC_FACTS.questions} questions and takes {DIAGNOSTIC_FACTS.minutesLabel}.
          </p>
        </div>

        <figure className="min-w-0" data-testid="about-course-path">
          <style>{STATIC_TRAIL_CSS}</style>
          <div
            className="sat-about-static max-h-[34rem] overflow-hidden rounded-3xl border bg-card p-5 shadow-[0_28px_70px_-32px_hsl(345_75%_31%/0.45)] [mask-image:linear-gradient(to_bottom,black_84%,transparent)] dark:shadow-[0_28px_70px_-32px_hsl(0_0%_0%/0.8)]"
            // `inert` is a React 19 boolean attribute; the installed types predate it
            {...({ inert: true } as object)}
          >
            <SatCoursePath
              domains={domains}
              activeDomain={DEMO_ACTIVE_DOMAIN}
              focusDomain={null}
              onFocusDomain={() => {}}
              initialDiagnosticCompletedAt={null}
              firstName={null}
            />
          </div>
          <figcaption className="mt-3 text-sm text-muted-foreground">Example student</figcaption>
        </figure>
      </div>
    </section>
  );
}
